import { Controller } from "@hotwired/stimulus"

// Auto-saving property editor for the selected block. Each field carries a
// data-field; on change it PATCHes that single prop via update_block, then asks
// the canvas to live-refresh so the change is visible immediately.
export default class extends Controller {
  static targets = ["status"]
  static values = {
    nodeId: String,
    pageId: String,
    region: String,
    messages: Object
  }

  save(event) {
    const el = event.target
    let value = el.type === "checkbox" ? el.checked : el.value
    if (el.type === "checkbox" && el.nextElementSibling) {
      el.nextElementSibling.textContent = el.checked ? this.message("enabled", "Enabled") : this.message("disabled", "Disabled")
    }
    if (el.dataset.valueType === "json") {
      try {
        value = JSON.parse(value)
      } catch (_error) {
        el.classList.add("ring-2", "ring-red-400")
        setTimeout(() => el.classList.remove("ring-2", "ring-red-400"), 800)
        return
      }
    }
    this.patch(el.dataset.field, value)
  }

  // Reorder the selected block within its parent (works at any depth).
  async move(event) {
    const body = new URLSearchParams({ node_id: this.nodeIdValue, direction: event.currentTarget.dataset.direction })
    if (this.regionValue) body.set("region", this.regionValue)
    else if (this.pageIdValue) body.set("page_id", this.pageIdValue)

    try {
      const response = await fetch("/seller/builder/blocks/move", {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, "Accept": "text/vnd.turbo-stream.html" },
        body
      })
      if (response.ok) window.Turbo?.renderStreamMessage(await response.text())
    } catch (_error) {
      // Non-fatal.
    }
  }

  // Color swatch writes into the sibling text field, then saves it.
  pickColor(event) {
    const text = event.target.nextElementSibling
    if (!text) return
    text.value = event.target.value
    this.patch(text.dataset.field, text.value)
  }

  patch(field, value) {
    if (!field) return

    window.dispatchEvent(new CustomEvent("builder:save-started"))
    const previous = window.__builderSaveQueue || Promise.resolve()
    const operation = previous.catch(() => {}).then(() => this.performPatch(field, value))
    window.__builderSaveQueue = operation.catch(() => {})
    return operation
  }

  async performPatch(field, value) {
    this.setStatus(this.message("saving", "Saving…"), "text-slate-400")

    const body = { node_id: this.nodeIdValue, inline: true, props: { [field]: value } }
    if (this.regionValue) body.region = this.regionValue
    else if (this.pageIdValue) body.page_id = this.pageIdValue

    try {
      const response = await fetch("/seller/builder/blocks/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(body)
      })
      if (!response.ok) throw new Error(`${response.status}`)
      this.setStatus(this.message("saved", "Saved"), "text-green-600")
      window.dispatchEvent(new CustomEvent("builder:preview-refresh"))
      window.dispatchEvent(new CustomEvent("builder:save-finished", { detail: { ok: true } }))
    } catch (_error) {
      this.setStatus(this.message("failed", "Could not save"), "text-red-500")
      window.dispatchEvent(new CustomEvent("builder:save-finished", { detail: { ok: false } }))
      throw _error
    }
  }

  setStatus(message, color) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("text-slate-400", "text-green-600", "text-red-500")
    this.statusTarget.classList.add(color)
  }

  message(key, fallback) {
    return this.hasMessagesValue ? (this.messagesValue[key] || fallback) : fallback
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
